/**
 * Object Manager Field Definitions
 *
 * Complete field definitions for all standard objects.
 * These fields are used by the Object Manager and throughout the application.
 */

import type { CustomField, PicklistValue } from '../queries/fieldQueries';

/**
 * Convert list view field type to Object Manager field type
 */
function mapFieldType(listViewType: string, fieldName: string): CustomField['fieldType'] {
  // Check for currency fields by name
  if (fieldName.toLowerCase().includes('revenue') || fieldName.toLowerCase().includes('amount')) {
    return 'currency';
  }

  // Map list view types to Object Manager types
  const typeMap: Record<string, CustomField['fieldType']> = {
    'text': 'text',
    'email': 'email',
    'phone': 'phone',
    'datetime': 'datetime',
    'date': 'date',
    'number': 'number',
    'badge': 'picklist',  // Badges are rendered as picklists
    'picklist': 'picklist',  // Picklist fields
    'multipicklist': 'multipicklist',  // Multi-select picklist fields
    'percent': 'percent',  // Percent fields
    'currency': 'currency',  // Currency fields
    'boolean': 'checkbox',
    'checkbox': 'checkbox',
    'lookup': 'lookup',  // Lookup fields for relationships
    'textarea': 'textarea',
    'url': 'url'
  };

  return typeMap[listViewType] || 'text';
}

/**
 * Determine if a field contains PII
 */
function isPIIField(fieldName: string): boolean {
  const piiFields = ['email', 'phone', 'mobilephone', 'address', 'billingaddress', 'shippingaddress', 'ssn', 'firstname', 'lastname', 'fullname'];
  return piiFields.some(pii => fieldName.toLowerCase().includes(pii));
}

/**
 * Determine if a field is required
 */
function isRequiredField(fieldName: string): boolean {
  const requiredFields = ['name', 'subject', 'status', 'email', 'casenumber'];
  return requiredFields.includes(fieldName.toLowerCase());
}

/**
 * PE-1470: Structured picklist values with metadata (e.g., Stage → Probability mapping)
 * Used for fields that need to trigger auto-fill behavior in forms
 *
 * Generic metadata format:
 * - targetField: The field API name to auto-fill (e.g., 'Probability__c')
 * - defaultValue: The value to set in the target field
 */
const structuredPicklistMap: Record<string, Record<string, PicklistValue[]>> = {
  'obj_opportunity': {
    // Stage values with Probability auto-fill metadata (PE-1470)
    'stage': [
      { value: 'Meeting Set', label: 'Meeting Set', metadata: { targetField: 'Probability__c', defaultValue: 10 } },
      { value: 'Discovery', label: 'Discovery', metadata: { targetField: 'Probability__c', defaultValue: 20 } },
      { value: 'Pitch', label: 'Pitch', metadata: { targetField: 'Probability__c', defaultValue: 40 } },
      { value: 'Evaluation', label: 'Evaluation', metadata: { targetField: 'Probability__c', defaultValue: 60 } },
      { value: 'Pending Deployment', label: 'Pending Deployment', metadata: { targetField: 'Probability__c', defaultValue: 80 } },
      { value: 'Closed Won', label: 'Closed Won', metadata: { targetField: 'Probability__c', defaultValue: 100 } },
      { value: 'Closed Lost', label: 'Closed Lost', metadata: { targetField: 'Probability__c', defaultValue: 0 } },
    ],
    'Stage__c': [
      { value: 'Meeting Set', label: 'Meeting Set', metadata: { targetField: 'Probability__c', defaultValue: 10 } },
      { value: 'Discovery', label: 'Discovery', metadata: { targetField: 'Probability__c', defaultValue: 20 } },
      { value: 'Pitch', label: 'Pitch', metadata: { targetField: 'Probability__c', defaultValue: 40 } },
      { value: 'Evaluation', label: 'Evaluation', metadata: { targetField: 'Probability__c', defaultValue: 60 } },
      { value: 'Pending Deployment', label: 'Pending Deployment', metadata: { targetField: 'Probability__c', defaultValue: 80 } },
      { value: 'Closed Won', label: 'Closed Won', metadata: { targetField: 'Probability__c', defaultValue: 100 } },
      { value: 'Closed Lost', label: 'Closed Lost', metadata: { targetField: 'Probability__c', defaultValue: 0 } },
    ],
  }
};

/**
 * Get structured picklist values with metadata for a field.
 * PE-1470: Returns PicklistValue[] with metadata for fields that support auto-mapping.
 */
export function getStructuredPicklistValues(fieldName: string, objectName: string): PicklistValue[] | undefined {
  return structuredPicklistMap[objectName]?.[fieldName];
}

/**
 * Get picklist values for badge/picklist fields.
 * Exported for use in fieldQueries enrichment (Gobodo fields may lack picklist metadata).
 * Returns simple string[] for backward compatibility. Use getStructuredPicklistValues for metadata.
 */
export function getPicklistValues(fieldName: string, objectName: string): string[] | undefined {
  // First check for structured values with metadata
  const structured = structuredPicklistMap[objectName]?.[fieldName];
  if (structured) {
    return structured.map(pv => pv.value);
  }

  const picklistMap: Record<string, Record<string, string[]>> = {
    'obj_lead': {
      'status': ['New', 'Contacted', 'Qualified', 'Unqualified'],
      'rating': ['Hot', 'Warm', 'Cold'],
      'leadSource': ['Website', 'Referral', 'Partner', 'Event', 'Cold Call', 'Other']
    },
    'obj_client': {
      'status': ['New', 'Active', 'Inactive', 'Churned'],
      'products': ['Credit Repair', 'ID Theft Recovery', 'Credit Development', 'Credit Monitoring', 'CyberSweep', 'Credit Block'],
      'cyberSweepStage': ['Pending Enrollments', 'Lacking Requirements', 'Awaiting Assignment', 'Pending Checklists', 'Awaiting Initial Report', 'Awaiting Final Report', 'Quarterly Report 1', 'Quarterly Report 2', 'Quarterly Report 3', 'Quarterly Report 4'],
      'cyberSweepStatus': ['Active', 'Pending', 'Completed', 'On Hold', 'Cancelled'],
      'auditStatus': ['Not Started', 'In Progress', 'Under Review', 'Completed', 'Failed'],
      'subscriptionStatus': ['Active', 'Pending', 'Suspended', 'Cancelled', 'Expired'],
      'experianStatus': ['Protected', 'Unprotected', 'Pending', 'Failed', 'Unknown'],
      'equifaxStatus': ['Protected', 'Unprotected', 'Pending', 'Failed', 'Unknown'],
      'transunionStatus': ['Protected', 'Unprotected', 'Pending', 'Failed', 'Unknown'],
      'subscriptionTier': ['Basic', 'Standard', 'Premium', 'Enterprise'],
      'paymentStatus': ['Current', 'Past Due', 'Failed', 'Pending', 'Cancelled']
    },
    'obj_contact': {
      'status': ['Active', 'Inactive']
    },
    'obj_opportunity': {
      'Stage__c': ['Meeting Set', 'Discovery', 'Pitch', 'Evaluation', 'Pending Deployment', 'Closed Won', 'Closed Lost'],
      'Type__c': ['New Business', 'Existing Business', 'Renewal'],
      'type': ['New Business', 'Existing Business', 'Renewal']
    },
    'obj_case': {
      'status': ['New', 'In Progress', 'Waiting on Customer', 'Escalated', 'Resolved', 'Closed'],
      'priority': ['Low', 'Medium', 'High', 'Critical'],
      'type': ['Question', 'Problem', 'Feature Request', 'Bug Report']
    },
    'obj_task': {
      'status': ['Open', 'In Progress', 'Completed', 'Deferred', 'Cancelled'],
      'priority': ['Low', 'Medium', 'High', 'Urgent'],
      'type': ['Action Item', 'Follow-up', 'Review', 'Call', 'Email', 'Meeting']
    },
    'obj_event': {
      'type': ['Call', 'Meeting', 'Email', 'Task'],
      'status': ['Planned', 'In Progress', 'Completed', 'Cancelled'],
      'priority': ['Low', 'Medium', 'High']
    },
    'obj_report': {
      'type': ['Sales', 'Service', 'Marketing', 'Finance', 'Operations', 'Custom'],
      'format': ['Table', 'Chart', 'Dashboard', 'Summary'],
      'status': ['Active', 'Scheduled', 'Draft', 'Archived']
    },
    'obj_user': {
      'status': ['Active', 'Inactive', 'Suspended'],
      'role': ['Admin', 'Manager', 'User', 'Viewer']
    },
    // CyberSweep Objects
    'obj_cs_audit': {
      'auditPhase': ['Discovery', 'Analysis', 'Remediation', 'Verification', 'Complete'],
      'status': ['Active', 'Completed', 'On Hold', 'Cancelled'],
      'auditType': ['Initial Audit', 'Follow-up Audit', 'Phase 1', 'Phase 2', 'Quarterly Audit', 'Annual Review', 'Special Request'],
      'phaseStatus': ['Not Started', 'P1 - In Progress', 'P1 - Road Block', 'P1 - Completed', 'P2 - In Progress', 'P2 - Road Block', 'P2 - Awaiting Assignment', 'P2 - Audit Checklist', 'Audit Completed']
    },
    'obj_paw_record': {
      'auditPhase': ['Phase 1', 'Phase 2'],
      'status': ['Pending', 'In Progress', 'Completed', 'Cancelled'],
      'findings': ['Info not found', 'Listings found and deletion requested', 'Site not working', 'Site deleted info', 'Site not responding', 'Site not honoring', 'Incomplete', 'Database not searchable', 'Listing/s not found'],
      'method': ['Manual Search', 'Automated Scan', 'API Query', 'Form Submission']
    },
    'obj_activity': {
      'Type__c': ['call', 'meeting', 'task', 'note', 'email', 'sms'],
      'RecordType__c': ['contact', 'account', 'lead', 'opportunity', 'case', 'client'],
      'Direction__c': ['inbound', 'outbound'],
      'Outcome__c': ['connected', 'voicemail', 'no_answer', 'busy', 'wrong_number'],
      'Source__c': ['manual', 'dialpad', 'email_system']
    }
  };

  return picklistMap[objectName]?.[fieldName];
}

/**
 * Generate system fields that are common to all objects
 */
function generateSystemFields(objectId: string, oneYearAgo: Date): CustomField[] {
  return [
    {
      id: `${objectId}_field_id`,
      objectId,
      fieldName: 'id',
      fieldLabel: 'Record ID',
      fieldType: 'text',
      description: 'Unique identifier for this record',
      helpText: 'System-generated unique identifier',
      required: true,
      unique: true,
      isPII: false,
      isStandard: true,
      isActive: true,
      isSystemField: true,
      isReadOnly: true,
      length: 50,
      fieldLevelSecurity: {
        visibleTo: ['all'],
        editableBy: []
      },
      createdBy: 'System',
      createdDate: oneYearAgo.toISOString(),
      lastModified: oneYearAgo.toISOString()
    },
    {
      id: `${objectId}_field_createdDate`,
      objectId,
      fieldName: 'createdDate',
      fieldLabel: 'Created Date',
      fieldType: 'datetime',
      description: 'Date and time when this record was created',
      helpText: 'Automatically set when record is created',
      required: true,
      unique: false,
      isPII: false,
      isStandard: true,
      isActive: true,
      isSystemField: true,
      isReadOnly: true,
      fieldLevelSecurity: {
        visibleTo: ['all'],
        editableBy: []
      },
      createdBy: 'System',
      createdDate: oneYearAgo.toISOString(),
      lastModified: oneYearAgo.toISOString()
    },
    {
      id: `${objectId}_field_createdBy`,
      objectId,
      fieldName: 'createdBy',
      fieldLabel: 'Created By',
      fieldType: 'lookup',
      description: 'User who created this record',
      helpText: 'Automatically set to current user when record is created',
      required: true,
      unique: false,
      isPII: false,
      isStandard: true,
      isActive: true,
      isSystemField: true,
      isReadOnly: true,
      lookupObject: 'User',
      fieldLevelSecurity: {
        visibleTo: ['all'],
        editableBy: []
      },
      createdBy: 'System',
      createdDate: oneYearAgo.toISOString(),
      lastModified: oneYearAgo.toISOString()
    },
    {
      id: `${objectId}_field_lastModifiedDate`,
      objectId,
      fieldName: 'lastModifiedDate',
      fieldLabel: 'Last Modified Date',
      fieldType: 'datetime',
      description: 'Date and time when this record was last modified',
      helpText: 'Automatically updated when record is modified',
      required: true,
      unique: false,
      isPII: false,
      isStandard: true,
      isActive: true,
      isSystemField: true,
      isReadOnly: true,
      fieldLevelSecurity: {
        visibleTo: ['all'],
        editableBy: []
      },
      createdBy: 'System',
      createdDate: oneYearAgo.toISOString(),
      lastModified: oneYearAgo.toISOString()
    },
    {
      id: `${objectId}_field_lastModifiedBy`,
      objectId,
      fieldName: 'lastModifiedBy',
      fieldLabel: 'Last Modified By',
      fieldType: 'lookup',
      description: 'User who last modified this record',
      helpText: 'Automatically updated when record is modified',
      required: true,
      unique: false,
      isPII: false,
      isStandard: true,
      isActive: true,
      isSystemField: true,
      isReadOnly: true,
      lookupObject: 'User',
      fieldLevelSecurity: {
        visibleTo: ['all'],
        editableBy: []
      },
      createdBy: 'System',
      createdDate: oneYearAgo.toISOString(),
      lastModified: oneYearAgo.toISOString()
    },
    {
      id: `${objectId}_field_owner`,
      objectId,
      fieldName: 'owner',
      fieldLabel: 'Owner',
      fieldType: 'lookup',
      description: 'User who owns this record',
      helpText: 'Record owner - can be changed by admins',
      required: true,
      unique: false,
      isPII: false,
      isStandard: true,
      isActive: true,
      isSystemField: true,
      isReadOnly: false, // Owner can be changed
      lookupObject: 'User',
      fieldLevelSecurity: {
        visibleTo: ['all'],
        editableBy: ['admin', 'manager']
      },
      createdBy: 'System',
      createdDate: oneYearAgo.toISOString(),
      lastModified: oneYearAgo.toISOString()
    }
  ];
}

/**
 * Generate field definitions for all objects
 */
export function generateObjectFields(): Record<string, CustomField[]> {
  const baseDate = new Date();
  const oneYearAgo = new Date(baseDate.getTime() - 365 * 24 * 60 * 60 * 1000);

  const fieldDefinitions: Record<string, Array<{
    fieldApiName: string;
    fieldLabel: string;
    fieldType: string;
    description?: string;
    isVisible: boolean;
    order: number;
    lookupObject?: string;
  }>> = {
    // LEAD FIELDS
    'obj_lead': [
      { fieldApiName: 'name', fieldLabel: 'Lead Name', fieldType: 'text', description: 'Full name of the lead', isVisible: true, order: 0 },
      { fieldApiName: 'company', fieldLabel: 'Company', fieldType: 'text', description: 'Company or organization name', isVisible: true, order: 1 },
      { fieldApiName: 'title', fieldLabel: 'Title', fieldType: 'text', description: 'Job title or position', isVisible: true, order: 2 },
      { fieldApiName: 'email', fieldLabel: 'Email', fieldType: 'email', description: 'Primary email address', isVisible: true, order: 3 },
      { fieldApiName: 'phone', fieldLabel: 'Phone', fieldType: 'phone', description: 'Primary phone number', isVisible: true, order: 4 },
      { fieldApiName: 'status', fieldLabel: 'Status', fieldType: 'badge', description: 'Current lead status', isVisible: true, order: 5 },
      { fieldApiName: 'leadSource', fieldLabel: 'Lead Source', fieldType: 'badge', description: 'How the lead was acquired', isVisible: true, order: 6 },
      { fieldApiName: 'rating', fieldLabel: 'Rating', fieldType: 'badge', description: 'Lead quality rating', isVisible: true, order: 7 },
      { fieldApiName: 'owner', fieldLabel: 'Owner', fieldType: 'text', description: 'Lead owner or assignee', isVisible: true, order: 8 },
      { fieldApiName: 'industry', fieldLabel: 'Industry', fieldType: 'text', description: 'Business industry', isVisible: false, order: 9 },
      { fieldApiName: 'numberOfEmployees', fieldLabel: 'Number of Employees', fieldType: 'number', description: 'Company size', isVisible: false, order: 10 },
      { fieldApiName: 'annualRevenue', fieldLabel: 'Annual Revenue', fieldType: 'number', description: 'Estimated annual revenue', isVisible: false, order: 11 },
      { fieldApiName: 'description', fieldLabel: 'Description', fieldType: 'textarea', description: 'Additional notes and information', isVisible: false, order: 12 },
      { fieldApiName: 'createdDate', fieldLabel: 'Created Date', fieldType: 'datetime', description: 'When the lead was created', isVisible: true, order: 13 },
      { fieldApiName: 'lastContact', fieldLabel: 'Last Contact', fieldType: 'datetime', description: 'Last contact date', isVisible: false, order: 14 },
    ],

    // CLIENT FIELDS
    'obj_client': [
      { fieldApiName: 'name', fieldLabel: 'Client Name', fieldType: 'text', description: 'Name of the client organization', isVisible: true, order: 0 },
      { fieldApiName: 'status', fieldLabel: 'Status', fieldType: 'badge', description: 'Current client status', isVisible: true, order: 1 },
      { fieldApiName: 'accountOwner', fieldLabel: 'Account Owner', fieldType: 'text', description: 'Account owner or manager', isVisible: true, order: 2 },
      { fieldApiName: 'lastContact', fieldLabel: 'Last Contact', fieldType: 'datetime', description: 'Most recent contact date', isVisible: true, order: 3 },
      { fieldApiName: 'industry', fieldLabel: 'Industry', fieldType: 'text', description: 'Business industry', isVisible: true, order: 4 },
      { fieldApiName: 'email', fieldLabel: 'Email', fieldType: 'email', description: 'Primary email address', isVisible: true, order: 5 },
      { fieldApiName: 'phone', fieldLabel: 'Phone', fieldType: 'phone', description: 'Primary phone number', isVisible: false, order: 6 },
      { fieldApiName: 'website', fieldLabel: 'Website', fieldType: 'url', description: 'Company website', isVisible: false, order: 7 },
      { fieldApiName: 'annualRevenue', fieldLabel: 'Annual Revenue', fieldType: 'number', description: 'Annual revenue amount', isVisible: false, order: 8 },
      { fieldApiName: 'numberOfEmployees', fieldLabel: 'Number of Employees', fieldType: 'number', description: 'Total employee count', isVisible: false, order: 9 },
      { fieldApiName: 'billingAddress', fieldLabel: 'Billing Address', fieldType: 'textarea', description: 'Billing address', isVisible: false, order: 10 },
      { fieldApiName: 'shippingAddress', fieldLabel: 'Shipping Address', fieldType: 'textarea', description: 'Shipping address', isVisible: false, order: 11 },
      { fieldApiName: 'description', fieldLabel: 'Description', fieldType: 'textarea', description: 'Additional information', isVisible: false, order: 12 },
      { fieldApiName: 'createdDate', fieldLabel: 'Created Date', fieldType: 'datetime', description: 'Record creation date', isVisible: false, order: 13 },
      { fieldApiName: 'lastModified', fieldLabel: 'Last Modified', fieldType: 'datetime', description: 'Last modification date', isVisible: false, order: 14 },
      { fieldApiName: 'activeProjects', fieldLabel: 'Active Projects', fieldType: 'number', description: 'Number of active projects', isVisible: false, order: 15 },
      { fieldApiName: 'totalRevenue', fieldLabel: 'Total Revenue', fieldType: 'number', description: 'Total revenue generated', isVisible: false, order: 16 },
      { fieldApiName: 'contractEndDate', fieldLabel: 'Contract End Date', fieldType: 'date', description: 'Contract expiration date', isVisible: false, order: 17 },
      // CyberSweep-specific fields
      { fieldApiName: 'firstName', fieldLabel: 'First Name', fieldType: 'text', description: 'Client first name', isVisible: false, order: 18 },
      { fieldApiName: 'lastName', fieldLabel: 'Last Name', fieldType: 'text', description: 'Client last name', isVisible: false, order: 19 },
      { fieldApiName: 'clientPersonalPhone', fieldLabel: 'Personal Phone', fieldType: 'phone', description: 'Personal phone number', isVisible: false, order: 20 },
      { fieldApiName: 'personalEmailAddress', fieldLabel: 'Personal Email', fieldType: 'email', description: 'Personal email address', isVisible: false, order: 21 },
      { fieldApiName: 'cyberSweepStartDate', fieldLabel: 'CyberSweep Start Date', fieldType: 'date', description: 'Program start date', isVisible: false, order: 22 },
      { fieldApiName: 'cyberSweepEndDate', fieldLabel: 'CyberSweep End Date', fieldType: 'date', description: 'Program end date', isVisible: false, order: 23 },
      { fieldApiName: 'cyberSweepStage', fieldLabel: 'CyberSweep Stage', fieldType: 'picklist', description: 'Current program stage', isVisible: false, order: 24 },
      { fieldApiName: 'cyberSweepStatus', fieldLabel: 'CyberSweep Status', fieldType: 'picklist', description: 'Program status', isVisible: false, order: 25 },
      { fieldApiName: 'auditId', fieldLabel: 'Audit ID', fieldType: 'text', description: 'Audit identifier', isVisible: false, order: 26 },
      { fieldApiName: 'lastAuditDate', fieldLabel: 'Last Audit Date', fieldType: 'date', description: 'Most recent audit date', isVisible: false, order: 27 },
      { fieldApiName: 'nextAuditDate', fieldLabel: 'Next Audit Date', fieldType: 'date', description: 'Scheduled next audit date', isVisible: false, order: 28 },
      { fieldApiName: 'auditStatus', fieldLabel: 'Audit Status', fieldType: 'picklist', description: 'Current audit status', isVisible: false, order: 29 },
      { fieldApiName: 'recordOwner', fieldLabel: 'Record Owner', fieldType: 'text', description: 'Record owner', isVisible: false, order: 30 },
      // Credit Block-specific fields
      { fieldApiName: 'accountName', fieldLabel: 'Account Name', fieldType: 'text', description: 'Account name', isVisible: false, order: 31 },
      { fieldApiName: 'dateOfBirth', fieldLabel: 'Date of Birth', fieldType: 'date', description: 'Client date of birth', isVisible: false, order: 32 },
      { fieldApiName: 'subscriptionStatus', fieldLabel: 'Subscription Status', fieldType: 'picklist', description: 'Current subscription status', isVisible: false, order: 33 },
      { fieldApiName: 'experianStatus', fieldLabel: 'Experian Status', fieldType: 'picklist', description: 'Experian bureau status', isVisible: false, order: 34 },
      { fieldApiName: 'equifaxStatus', fieldLabel: 'Equifax Status', fieldType: 'picklist', description: 'Equifax bureau status', isVisible: false, order: 35 },
      { fieldApiName: 'transunionStatus', fieldLabel: 'TransUnion Status', fieldType: 'picklist', description: 'TransUnion bureau status', isVisible: false, order: 36 },
      { fieldApiName: 'enrollmentDate', fieldLabel: 'Enrollment Date', fieldType: 'date', description: 'Date enrolled in Credit Block', isVisible: false, order: 37 },
      { fieldApiName: 'renewalDate', fieldLabel: 'Renewal Date', fieldType: 'date', description: 'Subscription renewal date', isVisible: false, order: 38 },
      { fieldApiName: 'subscriptionTier', fieldLabel: 'Subscription Tier', fieldType: 'picklist', description: 'Subscription tier level', isVisible: false, order: 39 },
      { fieldApiName: 'paymentStatus', fieldLabel: 'Payment Status', fieldType: 'picklist', description: 'Payment status', isVisible: false, order: 40 },
      { fieldApiName: 'mobile', fieldLabel: 'Mobile Phone', fieldType: 'phone', description: 'Mobile phone number', isVisible: false, order: 41 },
      { fieldApiName: 'teamEmail', fieldLabel: 'Team Email', fieldType: 'email', description: 'Team email address', isVisible: false, order: 42 },
      { fieldApiName: 'mailingStreet', fieldLabel: 'Mailing Street', fieldType: 'text', description: 'Mailing street address', isVisible: false, order: 43 },
      { fieldApiName: 'mailingCity', fieldLabel: 'Mailing City', fieldType: 'text', description: 'Mailing city', isVisible: false, order: 44 },
      { fieldApiName: 'mailingState', fieldLabel: 'Mailing State', fieldType: 'text', description: 'Mailing state', isVisible: false, order: 45 },
      { fieldApiName: 'mailingZip', fieldLabel: 'Mailing ZIP', fieldType: 'text', description: 'Mailing ZIP code', isVisible: false, order: 46 },
      { fieldApiName: 'managingFirm', fieldLabel: 'Managing Firm', fieldType: 'text', description: 'Managing firm name', isVisible: false, order: 47 },
      { fieldApiName: 'accountManager', fieldLabel: 'Account Manager', fieldType: 'text', description: 'Account manager name', isVisible: false, order: 48 },
      { fieldApiName: 'dayToDayContact', fieldLabel: 'Day-to-Day Contact', fieldType: 'text', description: 'Primary day-to-day contact', isVisible: false, order: 49 },
      { fieldApiName: 'createdBy', fieldLabel: 'Created By', fieldType: 'text', description: 'User who created the record', isVisible: false, order: 50 },
      { fieldApiName: 'lastModifiedBy', fieldLabel: 'Last Modified By', fieldType: 'text', description: 'User who last modified the record', isVisible: false, order: 51 },
      // Credit Management - Credit Scores
      { fieldApiName: 'initialScore', fieldLabel: 'Initial Credit Score', fieldType: 'number', description: 'Credit score at enrollment', isVisible: false, order: 52 },
      { fieldApiName: 'currentScore', fieldLabel: 'Current Credit Score', fieldType: 'number', description: 'Most recent credit score', isVisible: false, order: 53 },
      { fieldApiName: 'targetScore', fieldLabel: 'Target Credit Score', fieldType: 'number', description: 'Goal credit score', isVisible: false, order: 54 },
      { fieldApiName: 'scoreImprovement', fieldLabel: 'Score Improvement', fieldType: 'number', description: 'Points improved from initial score', isVisible: false, order: 55 },
      { fieldApiName: 'products', fieldLabel: 'Products', fieldType: 'multiselect', description: 'Enrolled products/services', isVisible: false, order: 56 },
      { fieldApiName: 'taxId', fieldLabel: 'Tax ID', fieldType: 'text', description: 'Tax identification number (sensitive)', isVisible: false, order: 57 },
    ],

    // CONTACT FIELDS
    'obj_contact': [
      { fieldApiName: 'name', fieldLabel: 'Name', fieldType: 'text', description: 'Contact name (standard field)', isVisible: true, order: 0 },
      { fieldApiName: 'fullName', fieldLabel: 'Full Name', fieldType: 'text', description: 'Contact full name', isVisible: true, order: 1 },
      { fieldApiName: 'firstName', fieldLabel: 'First Name', fieldType: 'text', description: 'First name', isVisible: false, order: 2 },
      { fieldApiName: 'lastName', fieldLabel: 'Last Name', fieldType: 'text', description: 'Last name', isVisible: false, order: 3 },
      { fieldApiName: 'title', fieldLabel: 'Title', fieldType: 'text', description: 'Job title', isVisible: true, order: 4 },
      { fieldApiName: 'account', fieldLabel: 'Account', fieldType: 'lookup', description: 'Associated account/client', isVisible: true, order: 5 },
      { fieldApiName: 'accountId', fieldLabel: 'Client (Lookup)', fieldType: 'lookup', description: 'Associated client (lookup field)', isVisible: true, order: 6 },
      { fieldApiName: 'clientName', fieldLabel: 'Client Name', fieldType: 'text', description: 'Associated client name (display)', isVisible: true, order: 7 },
      { fieldApiName: 'email', fieldLabel: 'Email', fieldType: 'email', description: 'Email address', isVisible: true, order: 8 },
      { fieldApiName: 'phone', fieldLabel: 'Phone', fieldType: 'phone', description: 'Primary phone', isVisible: true, order: 9 },
      { fieldApiName: 'mobile', fieldLabel: 'Mobile', fieldType: 'phone', description: 'Mobile phone (standard field)', isVisible: false, order: 10 },
      { fieldApiName: 'mobilePhone', fieldLabel: 'Mobile Phone', fieldType: 'phone', description: 'Mobile phone', isVisible: false, order: 11 },
      { fieldApiName: 'department', fieldLabel: 'Department', fieldType: 'text', description: 'Department', isVisible: true, order: 12 },
      { fieldApiName: 'isPrimary', fieldLabel: 'Primary Contact', fieldType: 'boolean', description: 'Is primary contact', isVisible: true, order: 13 },
      { fieldApiName: 'owner', fieldLabel: 'Owner', fieldType: 'text', description: 'Contact owner', isVisible: true, order: 14 },
      { fieldApiName: 'createdDate', fieldLabel: 'Created Date', fieldType: 'datetime', description: 'Creation date', isVisible: false, order: 15 },
      { fieldApiName: 'lastContact', fieldLabel: 'Last Contact', fieldType: 'datetime', description: 'Last contact date', isVisible: false, order: 16 },
      { fieldApiName: 'status', fieldLabel: 'Status', fieldType: 'badge', description: 'Contact status', isVisible: false, order: 17 },
      { fieldApiName: 'ssn', fieldLabel: 'Social Security Number', fieldType: 'text', description: 'Social security number (highly restricted)', isVisible: false, order: 18 },
      { fieldApiName: 'dateOfBirth', fieldLabel: 'Date of Birth', fieldType: 'date', description: 'Contact date of birth', isVisible: false, order: 19 },
      { fieldApiName: 'creditScore', fieldLabel: 'Credit Score', fieldType: 'number', description: 'Contact credit score', isVisible: false, order: 20 },
    ],

    // OPPORTUNITY FIELDS (using __c naming convention)
    'obj_opportunity': [
      { fieldApiName: 'Name__c', fieldLabel: 'Opportunity Name', fieldType: 'text', description: 'Name of the opportunity', isVisible: true, order: 0 },
      { fieldApiName: 'ClientName__c', fieldLabel: 'Client', fieldType: 'text', description: 'Associated client', isVisible: true, order: 1 },
      { fieldApiName: 'Amount__c', fieldLabel: 'Amount', fieldType: 'number', description: 'Deal amount', isVisible: true, order: 2 },
      { fieldApiName: 'Stage__c', fieldLabel: 'Stage', fieldType: 'picklist', description: 'Sales stage', isVisible: true, order: 3 },
      { fieldApiName: 'Probability__c', fieldLabel: 'Probability', fieldType: 'percent', description: 'Win probability', isVisible: true, order: 4 },
      { fieldApiName: 'CloseDate__c', fieldLabel: 'Close Date', fieldType: 'date', description: 'Expected close date', isVisible: true, order: 5 },
      { fieldApiName: 'Owner__c', fieldLabel: 'Owner', fieldType: 'lookup', lookupObject: 'User', description: 'Opportunity owner', isVisible: true, order: 6 },
      { fieldApiName: 'Type__c', fieldLabel: 'Type', fieldType: 'badge', description: 'Opportunity type', isVisible: false, order: 7 },
      { fieldApiName: 'LeadSource__c', fieldLabel: 'Lead Source', fieldType: 'text', description: 'Source of the lead', isVisible: false, order: 8 },
      { fieldApiName: 'NextStep__c', fieldLabel: 'Next Step', fieldType: 'text', description: 'Next action', isVisible: false, order: 9 },
      { fieldApiName: 'Description__c', fieldLabel: 'Description', fieldType: 'textarea', description: 'Opportunity details', isVisible: false, order: 10 },
      { fieldApiName: 'ContactName__c', fieldLabel: 'Contact', fieldType: 'text', description: 'Primary contact', isVisible: false, order: 11 },
      { fieldApiName: 'CreatedDate__c', fieldLabel: 'Created Date', fieldType: 'datetime', description: 'Creation date', isVisible: false, order: 12 },
      { fieldApiName: 'LastModifiedDate__c', fieldLabel: 'Last Modified', fieldType: 'datetime', description: 'Last modified', isVisible: false, order: 13 },
      { fieldApiName: 'ExpectedRevenue__c', fieldLabel: 'Expected Revenue', fieldType: 'number', description: 'Expected revenue', isVisible: false, order: 14 },
      { fieldApiName: 'CreatedBy__c', fieldLabel: 'Created By', fieldType: 'lookup', lookupObject: 'User', description: 'Creator', isVisible: false, order: 15 },
      { fieldApiName: 'CommissionRate__c', fieldLabel: 'Commission Rate', fieldType: 'percent', description: 'Commission percentage for this opportunity', isVisible: false, order: 16 },
    ],

    // CASE FIELDS
    'obj_case': [
      { fieldApiName: 'caseNumber', fieldLabel: 'Case Number', fieldType: 'text', description: 'Unique case number', isVisible: true, order: 0 },
      { fieldApiName: 'subject', fieldLabel: 'Subject', fieldType: 'text', description: 'Case subject', isVisible: true, order: 1 },
      { fieldApiName: 'accountId', fieldLabel: 'Client (Lookup)', fieldType: 'lookup', description: 'Associated client (lookup field)', isVisible: true, order: 2 },
      { fieldApiName: 'contactId', fieldLabel: 'Contact (Lookup)', fieldType: 'lookup', description: 'Associated contact (lookup field)', isVisible: true, order: 3 },
      { fieldApiName: 'status', fieldLabel: 'Status', fieldType: 'badge', description: 'Case status', isVisible: true, order: 4 },
      { fieldApiName: 'priority', fieldLabel: 'Priority', fieldType: 'badge', description: 'Priority level', isVisible: true, order: 5 },
      { fieldApiName: 'owner', fieldLabel: 'Owner', fieldType: 'text', description: 'Case owner', isVisible: true, order: 6 },
      { fieldApiName: 'createdDate', fieldLabel: 'Created Date', fieldType: 'datetime', description: 'Creation date', isVisible: true, order: 7 },
      { fieldApiName: 'type', fieldLabel: 'Type', fieldType: 'badge', description: 'Case type', isVisible: false, order: 8 },
      { fieldApiName: 'origin', fieldLabel: 'Origin', fieldType: 'text', description: 'Case origin', isVisible: false, order: 9 },
      { fieldApiName: 'productName', fieldLabel: 'Product', fieldType: 'text', description: 'Related product', isVisible: false, order: 10 },
      { fieldApiName: 'accountName', fieldLabel: 'Account Name', fieldType: 'text', description: 'Account name (display field)', isVisible: false, order: 11 },
      { fieldApiName: 'contactName', fieldLabel: 'Contact Name', fieldType: 'text', description: 'Contact name (display field)', isVisible: true, order: 12 },
      { fieldApiName: 'clientName', fieldLabel: 'Client Name', fieldType: 'text', description: 'Associated client (display)', isVisible: true, order: 12.5 },
      { fieldApiName: 'assignedTo', fieldLabel: 'Assigned To', fieldType: 'text', description: 'User or queue assigned to this case', isVisible: true, order: 12.6 },
      { fieldApiName: 'subType', fieldLabel: 'Sub-Type', fieldType: 'text', description: 'Case sub-type', isVisible: false, order: 12.7 },
      { fieldApiName: 'description', fieldLabel: 'Description', fieldType: 'textarea', description: 'Case description', isVisible: false, order: 13 },
      { fieldApiName: 'resolution', fieldLabel: 'Resolution', fieldType: 'textarea', description: 'Resolution summary', isVisible: false, order: 13.5 },
      { fieldApiName: 'closedDate', fieldLabel: 'Closed Date', fieldType: 'datetime', description: 'Close date', isVisible: false, order: 14 },
      { fieldApiName: 'lastModifiedDate', fieldLabel: 'Last Modified', fieldType: 'datetime', description: 'Last modified', isVisible: false, order: 15 },
      { fieldApiName: 'responseTime', fieldLabel: 'Response Time (hrs)', fieldType: 'number', description: 'Response time', isVisible: false, order: 16 },
      { fieldApiName: 'resolutionTime', fieldLabel: 'Resolution Time (hrs)', fieldType: 'number', description: 'Resolution time', isVisible: false, order: 17 },
      { fieldApiName: 'escalated', fieldLabel: 'Escalated', fieldType: 'boolean', description: 'Escalation flag', isVisible: false, order: 18 },
      { fieldApiName: 'resolutionNotes', fieldLabel: 'Resolution Notes', fieldType: 'textarea', description: 'Notes on case resolution', isVisible: false, order: 19 },
    ],

    // TASK FIELDS
    'obj_task': [
      { fieldApiName: 'title', fieldLabel: 'Task Title', fieldType: 'text', description: 'Task title or name', isVisible: true, order: 0 },
      { fieldApiName: 'status', fieldLabel: 'Status', fieldType: 'badge', description: 'Task status', isVisible: true, order: 1 },
      { fieldApiName: 'priority', fieldLabel: 'Priority', fieldType: 'badge', description: 'Priority level', isVisible: true, order: 2 },
      { fieldApiName: 'type', fieldLabel: 'Type', fieldType: 'badge', description: 'Task type', isVisible: true, order: 3 },
      { fieldApiName: 'assignee', fieldLabel: 'Assigned To', fieldType: 'text', description: 'Person assigned to this task', isVisible: true, order: 4 },
      { fieldApiName: 'dueDate', fieldLabel: 'Due Date', fieldType: 'date', description: 'Task due date', isVisible: true, order: 5 },
      { fieldApiName: 'description', fieldLabel: 'Description', fieldType: 'textarea', description: 'Task description', isVisible: false, order: 6 },
      { fieldApiName: 'assignedDate', fieldLabel: 'Assigned Date', fieldType: 'datetime', description: 'Date assigned', isVisible: false, order: 7 },
      { fieldApiName: 'completedDate', fieldLabel: 'Completed Date', fieldType: 'datetime', description: 'Date completed', isVisible: false, order: 8 },
      { fieldApiName: 'slaTarget', fieldLabel: 'SLA Target', fieldType: 'datetime', description: 'SLA target date/time', isVisible: false, order: 9 },
      { fieldApiName: 'source', fieldLabel: 'Source', fieldType: 'text', description: 'Task source', isVisible: false, order: 10 },
      { fieldApiName: 'clientId', fieldLabel: 'Client ID', fieldType: 'text', description: 'Related client ID', isVisible: false, order: 11 },
      { fieldApiName: 'clientName', fieldLabel: 'Client Name', fieldType: 'text', description: 'Related client name', isVisible: false, order: 12 },
      { fieldApiName: 'RelatedTo__c', fieldLabel: 'Related Case', fieldType: 'text', description: 'Related case ID', isVisible: false, order: 12.5 },
      { fieldApiName: 'createdBy', fieldLabel: 'Created By', fieldType: 'text', description: 'Task creator', isVisible: false, order: 13 },
      { fieldApiName: 'createdDate', fieldLabel: 'Created Date', fieldType: 'datetime', description: 'Creation date', isVisible: false, order: 14 },
      { fieldApiName: 'lastModifiedDate', fieldLabel: 'Last Modified', fieldType: 'datetime', description: 'Last modified date', isVisible: false, order: 15 },
    ],

    // EVENT FIELDS
    'obj_event': [
      { fieldApiName: 'subject', fieldLabel: 'Subject', fieldType: 'text', description: 'Event subject', isVisible: true, order: 0 },
      { fieldApiName: 'relatedToName', fieldLabel: 'Related To', fieldType: 'text', description: 'Related record', isVisible: true, order: 1 },
      { fieldApiName: 'startDateTime', fieldLabel: 'Start Date/Time', fieldType: 'datetime', description: 'Start time', isVisible: true, order: 2 },
      { fieldApiName: 'endDateTime', fieldLabel: 'End Date/Time', fieldType: 'datetime', description: 'End time', isVisible: true, order: 3 },
      { fieldApiName: 'type', fieldLabel: 'Type', fieldType: 'badge', description: 'Event type', isVisible: true, order: 4 },
      { fieldApiName: 'status', fieldLabel: 'Status', fieldType: 'badge', description: 'Event status', isVisible: true, order: 5 },
      { fieldApiName: 'organizer', fieldLabel: 'Organizer', fieldType: 'text', description: 'Event organizer', isVisible: false, order: 6 },
      { fieldApiName: 'location', fieldLabel: 'Location', fieldType: 'text', description: 'Event location', isVisible: false, order: 7 },
      { fieldApiName: 'isAllDay', fieldLabel: 'All Day Event', fieldType: 'boolean', description: 'All day flag', isVisible: false, order: 8 },
      { fieldApiName: 'description', fieldLabel: 'Description', fieldType: 'textarea', description: 'Event description', isVisible: false, order: 9 },
      { fieldApiName: 'createdDate', fieldLabel: 'Created Date', fieldType: 'datetime', description: 'Creation date', isVisible: false, order: 10 },
      { fieldApiName: 'priority', fieldLabel: 'Priority', fieldType: 'badge', description: 'Priority level', isVisible: false, order: 11 },
    ],

    // REPORT FIELDS
    'obj_report': [
      { fieldApiName: 'name', fieldLabel: 'Report Name', fieldType: 'text', description: 'Report name', isVisible: true, order: 0 },
      { fieldApiName: 'objectName', fieldLabel: 'Object', fieldType: 'text', description: 'Report object', isVisible: true, order: 1 },
      { fieldApiName: 'type', fieldLabel: 'Category', fieldType: 'badge', description: 'Report category (Sales, Service, Marketing, etc.)', isVisible: true, order: 2 },
      { fieldApiName: 'format', fieldLabel: 'Format', fieldType: 'text', description: 'Report format (Table, Chart, Dashboard)', isVisible: true, order: 3 },
      { fieldApiName: 'lastRunDate', fieldLabel: 'Last Run', fieldType: 'datetime', description: 'Last run time', isVisible: true, order: 4 },
      { fieldApiName: 'status', fieldLabel: 'Status', fieldType: 'badge', description: 'Report status', isVisible: true, order: 5 },
      { fieldApiName: 'createdBy', fieldLabel: 'Created By', fieldType: 'text', description: 'Report creator', isVisible: true, order: 6 },
      { fieldApiName: 'description', fieldLabel: 'Description', fieldType: 'textarea', description: 'Report description', isVisible: false, order: 7 },
      { fieldApiName: 'frequency', fieldLabel: 'Frequency', fieldType: 'text', description: 'Run frequency', isVisible: false, order: 8 },
    ],

    // USER FIELDS
    'obj_user': [
      { fieldApiName: 'name', fieldLabel: 'Name', fieldType: 'text', description: 'User name', isVisible: true, order: 0 },
      { fieldApiName: 'email', fieldLabel: 'Email', fieldType: 'email', description: 'Email address', isVisible: true, order: 1 },
      { fieldApiName: 'role', fieldLabel: 'Role', fieldType: 'badge', description: 'User role', isVisible: true, order: 2 },
      { fieldApiName: 'department', fieldLabel: 'Department', fieldType: 'text', description: 'Department', isVisible: true, order: 3 },
      { fieldApiName: 'status', fieldLabel: 'Status', fieldType: 'badge', description: 'User status', isVisible: true, order: 4 },
      { fieldApiName: 'phone', fieldLabel: 'Phone', fieldType: 'phone', description: 'Phone number', isVisible: false, order: 5 },
      { fieldApiName: 'title', fieldLabel: 'Title', fieldType: 'text', description: 'Job title', isVisible: false, order: 6 },
      { fieldApiName: 'manager', fieldLabel: 'Manager', fieldType: 'text', description: 'Manager name', isVisible: false, order: 7 },
      { fieldApiName: 'lastLogin', fieldLabel: 'Last Login', fieldType: 'datetime', description: 'Last login time', isVisible: false, order: 8 },
      { fieldApiName: 'createdDate', fieldLabel: 'Created Date', fieldType: 'datetime', description: 'Creation date', isVisible: false, order: 9 },
      { fieldApiName: 'isActive', fieldLabel: 'Active', fieldType: 'boolean', description: 'Active flag', isVisible: false, order: 10 },
      { fieldApiName: 'profilePicture', fieldLabel: 'Profile Picture', fieldType: 'url', description: 'Profile image', isVisible: false, order: 11 },
    ],

    // CS AUDIT FIELDS (CyberSweep)
    'obj_cs_audit': [
      { fieldApiName: 'name', fieldLabel: 'Audit Number', fieldType: 'text', description: 'Auto-generated audit number (e.g., CyberAudit-0001)', isVisible: true, order: 0 },
      { fieldApiName: 'clientId', fieldLabel: 'Client', fieldType: 'lookup', description: 'Related CyberSweep client', isVisible: true, order: 1 },
      { fieldApiName: 'clientName', fieldLabel: 'Account', fieldType: 'text', description: 'Client name display field (read-only)', isVisible: true, order: 2 },
      { fieldApiName: 'auditPhase', fieldLabel: 'Audit Phase', fieldType: 'badge', description: 'Current phase of the audit process', isVisible: true, order: 3 },
      { fieldApiName: 'phaseStatus', fieldLabel: 'Phase Status', fieldType: 'badge', description: 'Detailed phase status', isVisible: true, order: 4 },
      { fieldApiName: 'status', fieldLabel: 'Status', fieldType: 'badge', description: 'Overall status of the audit', isVisible: true, order: 5 },
      { fieldApiName: 'auditType', fieldLabel: 'Audit Type', fieldType: 'picklist', description: 'Type of audit being conducted', isVisible: true, order: 6 },
      { fieldApiName: 'fiscalYear', fieldLabel: 'Fiscal Year', fieldType: 'text', description: 'Fiscal year of the audit', isVisible: true, order: 7 },
      { fieldApiName: 'assignedAnalyst', fieldLabel: 'Auditor', fieldType: 'lookup', lookupObject: 'User', description: 'Auditor assigned to this audit', isVisible: true, order: 8 },
      { fieldApiName: 'dueDate', fieldLabel: 'Due Date', fieldType: 'date', description: 'Overall audit due date', isVisible: true, order: 9 },
      { fieldApiName: 'phase1DueDate', fieldLabel: 'Phase 1 Due Date', fieldType: 'date', description: 'Phase 1 due date', isVisible: false, order: 10 },
      { fieldApiName: 'phase2DueDate', fieldLabel: 'Phase 2 Due Date', fieldType: 'date', description: 'Phase 2 due date', isVisible: false, order: 11 },
      { fieldApiName: 'completionDate', fieldLabel: 'Completion Date', fieldType: 'date', description: 'Date the audit was completed', isVisible: false, order: 12 },
      { fieldApiName: 'roadblockNotes', fieldLabel: 'Roadblock Notes', fieldType: 'textarea', description: 'Notes about blockers or issues encountered', isVisible: false, order: 13 },
      { fieldApiName: 'websiteCount', fieldLabel: 'Website Count', fieldType: 'number', description: 'Number of websites in this audit', isVisible: false, order: 14 },
      { fieldApiName: 'createdDate', fieldLabel: 'Created Date', fieldType: 'datetime', description: 'When the audit was created', isVisible: false, order: 15 },
      { fieldApiName: 'lastModified', fieldLabel: 'Last Modified', fieldType: 'datetime', description: 'Last modification date', isVisible: false, order: 16 },
    ],

    // ACTIVITY FIELDS (for Gobodo Activity__c object)
    'obj_activity': [
      { fieldApiName: 'Type__c', fieldLabel: 'Type', fieldType: 'picklist', description: 'Activity type (call, meeting, task, note, email, sms)', isVisible: true, order: 0 },
      { fieldApiName: 'Subject__c', fieldLabel: 'Subject', fieldType: 'text', description: 'Activity subject or title', isVisible: true, order: 1 },
      { fieldApiName: 'Description__c', fieldLabel: 'Description', fieldType: 'textarea', description: 'Activity notes and details', isVisible: true, order: 2 },
      { fieldApiName: 'RecordId__c', fieldLabel: 'Related Record', fieldType: 'lookup', description: 'ID of the related record (Contact, Account, Opportunity, etc.)', isVisible: true, order: 3 },
      { fieldApiName: 'RecordType__c', fieldLabel: 'Record Type', fieldType: 'picklist', description: 'Type of related record (contact, account, lead, opportunity, case, client)', isVisible: true, order: 4 },
      { fieldApiName: 'UserId__c', fieldLabel: 'User', fieldType: 'lookup', description: 'User who created/performed the activity', isVisible: true, order: 5 },
      { fieldApiName: 'UserName__c', fieldLabel: 'User Name', fieldType: 'text', description: 'User name (denormalized for display)', isVisible: true, order: 6 },
      { fieldApiName: 'CreatedAt__c', fieldLabel: 'Created At', fieldType: 'datetime', description: 'When the activity was logged', isVisible: true, order: 7 },
      { fieldApiName: 'ScheduledAt__c', fieldLabel: 'Scheduled At', fieldType: 'datetime', description: 'Scheduled date/time for meetings', isVisible: false, order: 8 },
      { fieldApiName: 'Timezone__c', fieldLabel: 'Timezone', fieldType: 'text', description: 'IANA timezone (e.g., America/New_York)', isVisible: false, order: 9 },
      { fieldApiName: 'Duration__c', fieldLabel: 'Duration', fieldType: 'number', description: 'Duration in minutes', isVisible: true, order: 10 },
      { fieldApiName: 'Location__c', fieldLabel: 'Location', fieldType: 'text', description: 'Meeting location or video link', isVisible: false, order: 11 },
      { fieldApiName: 'Direction__c', fieldLabel: 'Direction', fieldType: 'picklist', description: 'Call direction (inbound, outbound)', isVisible: false, order: 12 },
      { fieldApiName: 'Outcome__c', fieldLabel: 'Outcome', fieldType: 'picklist', description: 'Call outcome (connected, voicemail, no_answer, busy, wrong_number)', isVisible: false, order: 13 },
      { fieldApiName: 'RecipientEmail__c', fieldLabel: 'Recipient Email', fieldType: 'email', description: 'Email recipient', isVisible: false, order: 14 },
      { fieldApiName: 'RecipientPhone__c', fieldLabel: 'Recipient Phone', fieldType: 'phone', description: 'Phone number called', isVisible: false, order: 15 },
      { fieldApiName: 'RecipientName__c', fieldLabel: 'Recipient Name', fieldType: 'text', description: 'Recipient name', isVisible: false, order: 16 },
      { fieldApiName: 'Source__c', fieldLabel: 'Source', fieldType: 'picklist', description: 'Activity source (manual, dialpad, email_system)', isVisible: false, order: 17 },
      { fieldApiName: 'SourceApp__c', fieldLabel: 'Source App', fieldType: 'text', description: 'Source application (regal_main, credit_management, cybersweep, credit_block)', isVisible: false, order: 18 },
      { fieldApiName: 'TemplateId__c', fieldLabel: 'Template ID', fieldType: 'text', description: 'Email template ID if applicable', isVisible: false, order: 19 },
      { fieldApiName: 'TemplateName__c', fieldLabel: 'Template Name', fieldType: 'text', description: 'Email template name if applicable', isVisible: false, order: 20 },
    ],

    // PAW RECORD FIELDS (CyberSweep) - Matches Salesforce PAW record layout
    'obj_paw_record': [
      { fieldApiName: 'name', fieldLabel: 'Performed Audit Website Name', fieldType: 'text', description: 'Auto-generated PAW number (e.g., PAW-1400233)', isVisible: true, order: 0 },
      { fieldApiName: 'csAuditId', fieldLabel: 'CS Audit', fieldType: 'lookup', description: 'Related CS Audit record', isVisible: true, order: 1 },
      { fieldApiName: 'auditTemplate', fieldLabel: 'Audit Template', fieldType: 'lookup', description: 'Template used for this audit (e.g., Initial Audit)', isVisible: true, order: 2 },
      { fieldApiName: 'website', fieldLabel: 'Website', fieldType: 'url', description: 'Data broker website being monitored', isVisible: true, order: 3 },
      { fieldApiName: 'auditPhase', fieldLabel: 'Audit Phase', fieldType: 'picklist', description: 'Current audit phase (Phase 1 or Phase 2)', isVisible: true, order: 4 },
      { fieldApiName: 'findings', fieldLabel: 'Findings', fieldType: 'text', description: 'Audit findings for this website', isVisible: true, order: 5 },
      { fieldApiName: 'method', fieldLabel: 'Method', fieldType: 'text', description: 'Method used (e.g., Site Opt-out form)', isVisible: true, order: 6 },
      { fieldApiName: 'piiFound', fieldLabel: 'PII Found', fieldType: 'text', description: 'Personal information found on the website', isVisible: true, order: 7 },
      { fieldApiName: 'phase2Required', fieldLabel: 'Phase 2 Required', fieldType: 'checkbox', description: 'Whether Phase 2 review is required', isVisible: true, order: 8 },
      { fieldApiName: 'clientInfoLinks', fieldLabel: 'Link(s) for client information', fieldType: 'textarea', description: 'URLs where client information was found', isVisible: true, order: 9 },
      { fieldApiName: 'createdBy', fieldLabel: 'Created By', fieldType: 'text', description: 'User who created the record', isVisible: true, order: 10 },
      { fieldApiName: 'lastModifiedBy', fieldLabel: 'Last Modified By', fieldType: 'text', description: 'User who last modified the record', isVisible: true, order: 11 },
      { fieldApiName: 'clientId', fieldLabel: 'Client', fieldType: 'lookup', description: 'Related CyberSweep client', isVisible: false, order: 12 },
      { fieldApiName: 'clientName', fieldLabel: 'Client Name', fieldType: 'text', description: 'Client name display field', isVisible: false, order: 13 },
      { fieldApiName: 'websiteUrl', fieldLabel: 'Website URL', fieldType: 'url', description: 'Website URL being monitored', isVisible: false, order: 14 },
      { fieldApiName: 'status', fieldLabel: 'Status', fieldType: 'picklist', description: 'Current status of the PAW record', isVisible: false, order: 15 },
    ],
  };

  // Convert to CustomField format
  const result: Record<string, CustomField[]> = {};

  Object.entries(fieldDefinitions).forEach(([objectId, fields]) => {
    // Generate system fields first
    const systemFields = generateSystemFields(objectId, oneYearAgo);

    // Then add object-specific fields
    const objectFields = fields.map((field, index) => {
      const fieldType = mapFieldType(field.fieldType, field.fieldApiName);
      const isStandard = true; // All these are standard fields

      return {
        id: `field_${objectId}_${String(index + 1).padStart(3, '0')}`,
        objectId,
        fieldName: field.fieldApiName,
        fieldLabel: field.fieldLabel,
        fieldType,
        description: field.description,
        required: isRequiredField(field.fieldApiName),
        unique: field.fieldApiName === 'email' || field.fieldApiName === 'caseNumber',
        isPII: isPIIField(field.fieldApiName),
        isStandard,
        isActive: true,
        isSystemField: false, // Regular fields are not system fields
        isReadOnly: false,    // Regular fields can be edited
        picklistValues: getPicklistValues(field.fieldApiName, objectId),
        ...(field.lookupObject != null && { lookupObject: field.lookupObject }),
        fieldLevelSecurity: {
          visibleTo: ['all'],
          editableBy: ['admin', 'user']
        },
        createdBy: 'System',
        createdDate: oneYearAgo.toISOString(),
        lastModified: oneYearAgo.toISOString()
      };
    });

    // Combine system fields + object fields
    // Filter out any object fields that duplicate system fields
    const systemFieldNames = systemFields.map(f => f.fieldName);
    const filteredObjectFields = objectFields.filter(f => !systemFieldNames.includes(f.fieldName));

    result[objectId] = [...systemFields, ...filteredObjectFields];
  });

  return result;
}

// Generate and export all fields
export const allObjectFields = generateObjectFields();
